import { useDisruptionStyles, useModuleBEM } from "@b2bportal/core-themes";
import { CoreDisruptionComponent } from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import ExternalLinkContainer from "../../../SharedComponents/ExternalLinkContainer/ExternalLinkContainer";
import { DisruptionExerciseButton } from "../DisruptionExerciseButton";
import { DisruptionExerciseButtonType } from "../DisruptionExerciseButton/DisruptionExerciseButton";
import {
  DisruptionSupportPhone,
  useDisruptionSupportPhone,
} from "../DisruptionSupportPhone";
import defaultStyles from "./DisruptionExerciseFooter.module.scss";

export interface DisruptionExerciseFooterProps {
  onSupportClick?: () => void;
  onRefundClick?: () => void;
  termsLink?: string;
  displayLinkInModal?: boolean;
  hideFooterDivider?: boolean;
  helpMessage?: string;
  showSupportPhone?: boolean;
}

export const DisruptionExerciseFooter = (
  props: DisruptionExerciseFooterProps
) => {
  const { t } = useI18nContext();
  const COMPONENT_KEY = CoreDisruptionComponent.DisruptionExerciseFooter;
  const styles = useDisruptionStyles(COMPONENT_KEY, defaultStyles);
  const [_, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { isDesktopAndUp } = useDeviceType();

  const {
    hideFooterDivider = false,
    helpMessage = t("core-disruption.disruptionUniversalExercise.needHelp"),
    showSupportPhone = false,
  } = props;

  const { supportPhone, supportPhoneHref, contactSupportAt } =
    useDisruptionSupportPhone();
  const usePhoneSupport = Boolean(
    showSupportPhone && supportPhone && props.onSupportClick
  );

  const supportButton = props.onSupportClick ? (
    <div className={cn("supportButtonContainer")}>
      {usePhoneSupport ? (
        <span className={cn("text", { supportPhone: usePhoneSupport })}>
          <DisruptionSupportPhone
            contactSupportAt={contactSupportAt}
            linkClassName={cn("supportPhoneLink")}
            supportPhone={supportPhone}
            supportPhoneHref={supportPhoneHref}
          >
            {helpMessage}
          </DisruptionSupportPhone>
        </span>
      ) : (
        <DisruptionExerciseButton
          onClick={props.onSupportClick}
          title={t(
            "core-disruption.disruptionUniversalExercise.contactSupport"
          )}
          disabled={false}
          type={DisruptionExerciseButtonType.Clear}
        />
      )}
    </div>
  ) : null;

  const refundButton = props.onRefundClick ? (
    <div className={cn("refundButtonContainer")}>
      <DisruptionExerciseButton
        onClick={props.onRefundClick}
        title={t("core-disruption.disruptionUniversalExercise.requestRefund")}
        disabled={false}
        type={DisruptionExerciseButtonType.Clear}
      />
    </div>
  ) : null;

  const termsButton =
    props.termsLink != null && !isDesktopAndUp ? (
      <div className={cn("termsButtonContainer")}>
        <ExternalLinkContainer
          linkUrl={props.termsLink}
          content={
            <DisruptionExerciseButton
              onClick={() => {
                // no action
              }}
              title={t(
                "core-disruption.disruptionUniversalExercise.termsAndConditions"
              )}
              disabled={false}
              type={DisruptionExerciseButtonType.Clear}
            />
          }
          displayInModal={props.displayLinkInModal ?? true}
        />
      </div>
    ) : null;

  const footer = (
    <div
      className={cn("disruptionFooterContent", {
        supportPhone: usePhoneSupport,
      })}
    >
      {!usePhoneSupport && <span className={cn("text")}>{helpMessage}</span>}
      {termsButton}
      {supportButton}
      {props.onRefundClick && (
        <div className={cn("refundContainer")}>
          <span className={cn("text")}>
            {t("core-disruption.disruptionUniversalExercise.or")}
          </span>
          {refundButton}
        </div>
      )}
    </div>
  );

  return (
    <div className={cn("disruptionFooterRoot")}>
      {!hideFooterDivider && <hr></hr>}
      {footer}
    </div>
  );
};

export default DisruptionExerciseFooter;
